import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import {
	Calendar,
	Clock,
	Users,
	Building,
	Trash2,
	AlertCircle
} from 'lucide-react';
import { validateConditionHasActions } from '@/lib/conditionValidation';

interface ConditionFormData {
	conditionId?: string;
	startDate?: string;
	endDate?: string;
	arrivingSince?: string;
	arrivingUntil?: string;
	leavingSince?: string;
	leavingUntil?: string;
	weeklyDateSetId?: number;
	weeklyDateSetName?: string;
	appliesOnHolidays: boolean;
	minStayMinutes?: number;
	maxStayMinutes?: number;
	appliesToSpecificVehicleTypes: boolean;
	appliesToSpecificCustomerCategories: boolean;
	appliesToSpecificPaymentMethods: boolean;
	isOverstay: boolean;
	isContractOverstay: boolean;
	selectedVehicleTypes: string[];
	selectedCustomerCategories: string[];
	selectedPaymentMethods: string[];
	selectedDaysOfWeek: string[];
	operator: 'include' | 'exclude';
}

interface ConditionCardProps {
	condition: ConditionFormData;
	onEdit: () => void;
	onDelete: () => void;
	onToggleOperator: () => void;
}

export const ConditionCard: React.FC<ConditionCardProps> = ({
	condition,
	onEdit,
	onDelete,
	onToggleOperator
}) => {
	const { t } = useTranslation('scenarios');
	const getConditionIcon = () => {
		if (
			condition.arrivingSince ||
			condition.arrivingUntil ||
			condition.leavingSince ||
			condition.leavingUntil
		) {
			return <Clock className="w-4 h-4" />;
		}
		if (condition.startDate || condition.endDate) {
			return <Calendar className="w-4 h-4" />;
		}
		if (
			condition.minStayMinutes ||
			condition.maxStayMinutes ||
			condition.isOverstay
		) {
			return <AlertCircle className="w-4 h-4" />;
		}
		if (
			condition.appliesToSpecificVehicleTypes ||
			condition.appliesToSpecificCustomerCategories
		) {
			return <Users className="w-4 h-4" />;
		}
		return <Building className="w-4 h-4" />;
	};

	const getConditionSummary = () => {
		const parts: string[] = [];

		if (condition.startDate || condition.endDate) {
			if (condition.startDate && condition.endDate) {
				parts.push(t('conditionCard.dateRange', { start: condition.startDate, end: condition.endDate }));
			} else if (condition.startDate) {
				parts.push(t('conditionCard.fromDate', { date: condition.startDate }));
			} else if (condition.endDate) {
				parts.push(t('conditionCard.untilDate', { date: condition.endDate }));
			}
		}

		if (condition.arrivingSince || condition.arrivingUntil) {
			if (condition.arrivingSince && condition.arrivingUntil) {
				parts.push(
					t('conditionCard.arrivingBetween', { since: condition.arrivingSince, until: condition.arrivingUntil })
				);
			} else if (condition.arrivingSince) {
				parts.push(t('conditionCard.arrivingAfter', { time: condition.arrivingSince }));
			} else if (condition.arrivingUntil) {
				parts.push(t('conditionCard.arrivingBefore', { time: condition.arrivingUntil }));
			}
		}

		if (condition.leavingSince || condition.leavingUntil) {
			if (condition.leavingSince && condition.leavingUntil) {
				parts.push(
					t('conditionCard.leavingBetween', { since: condition.leavingSince, until: condition.leavingUntil })
				);
			} else if (condition.leavingSince) {
				parts.push(t('conditionCard.leavingAfter', { time: condition.leavingSince }));
			} else if (condition.leavingUntil) {
				parts.push(t('conditionCard.leavingBefore', { time: condition.leavingUntil }));
			}
		}

		if (condition.minStayMinutes || condition.maxStayMinutes) {
			if (condition.minStayMinutes && condition.maxStayMinutes) {
				parts.push(
					t('conditionCard.stayBetween', { min: condition.minStayMinutes, max: condition.maxStayMinutes })
				);
			} else if (condition.minStayMinutes) {
				parts.push(t('conditionCard.minStay', { minutes: condition.minStayMinutes }));
			} else if (condition.maxStayMinutes) {
				parts.push(t('conditionCard.maxStay', { minutes: condition.maxStayMinutes }));
			}
		}

		if (condition.appliesOnHolidays) {
			parts.push(t('conditionCard.appliesOnHolidays'));
		}

		if (condition.isOverstay) {
			parts.push(t('conditionCard.overstayCondition'));
		}

		if (condition.isContractOverstay) {
			parts.push(t('conditionCard.contractOverstay'));
		}

		if (
			condition.appliesToSpecificVehicleTypes &&
			condition.selectedVehicleTypes.length > 0
		) {
			parts.push(
				t('conditionCard.vehicleTypes', { count: condition.selectedVehicleTypes.length })
			);
		}

		if (
			condition.appliesToSpecificCustomerCategories &&
			condition.selectedCustomerCategories.length > 0
		) {
			parts.push(
				t('conditionCard.customerCategories', { count: condition.selectedCustomerCategories.length })
			);
		}

		if (
			condition.appliesToSpecificPaymentMethods &&
			condition.selectedPaymentMethods.length > 0
		) {
			parts.push(
				t('conditionCard.paymentMethods', { count: condition.selectedPaymentMethods.length })
			);
		}

		if (condition.weeklyDateSetName && condition.weeklyDateSetName.toLowerCase() !== 'custom') {
			parts.push(t('conditionCard.weeklyDateSet', { name: condition.weeklyDateSetName }));
		}

		if (condition.selectedDaysOfWeek.length > 0) {
			parts.push(t('conditionCard.daysOfWeek', { count: condition.selectedDaysOfWeek.length }));
		}

		return parts.length > 0
			? parts.join(' • ')
			: t('conditionCard.noActionsConfigured');
	};

	return (
		<div
			className="p-4 border rounded-lg border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100/80 dark:hover:bg-gray-700/50 transition-colors"
			onClick={onEdit}
		>
			<div className="flex items-center gap-3">
				<div className="flex-1">
					<div className="flex items-start gap-3">
						<div className="flex items-center gap-2">
							{getConditionIcon()}
							{!validateConditionHasActions(condition) && (
								<Badge
									variant="destructive"
									className="text-xs"
								>
									<AlertCircle className="w-3 h-3 mr-1" />
									No Actions
								</Badge>
							)}
						</div>
						<div className="flex-1">
							<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{getConditionSummary()}
							</p>
						</div>
					</div>
				</div>

				<div className="flex items-center gap-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={(e) => { e.stopPropagation(); onToggleOperator(); }}
					>
						{t('conditionCard.switchTo')}{' '}
						{condition.operator === 'include'
							? t('conditionCard.exclude')
							: t('conditionCard.include')}
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={(e) => { e.stopPropagation(); onDelete(); }}
					>
						<Trash2 className="w-4 h-4" />
					</Button>
				</div>
			</div>
		</div>
	);
};

